import { useDeepCompareMemo } from "@react-hookz/web";
import creditCardType from "credit-card-type";
import type { CreditCardType } from "credit-card-type/src/types";
import { friendlyFormatIBAN } from "ibantools";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  GlobalError,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrevious } from "react-use";

import { logger } from "c7-logger/index";

import { cardNumberValidator } from "cc-tools/card-number-validator";
import {
  niceTypesToCreditCardTypeCardBrandId,
  padExpirationMonthValue,
  prettifyExpirationValue,
  prettifyUICardNumber,
} from "cc-tools/helpers";
import "cc-tools/setup-credit-card";

import CheckExampleEn from "chameleon/icons/check-example-en";
import CheckExampleEsUS from "chameleon/icons/check-example-es-US";
import {
  CVVInput,
  CardNetworkRadioGroup,
  CardNumberInput,
  Checkbox,
  Select,
  TextInput,
} from "chameleon/lib/components";
import { CreditCardNetwork } from "chameleon/lib/components/display-cards/display-cards";

import {
  cardNumberMetaDiuAtom,
  fetchingCardTypeAtom,
  showCardNumberSpinnerAtom,
} from "checkout-experience/atoms/payment-atoms";
import { MapFieldFrame } from "checkout-experience/components/forms/field-frame-mappers/map-field-frame";
import { FormConsentCheckbox } from "checkout-experience/components/forms/form-consent-checkbox";
import { FormCurrentAddress } from "checkout-experience/components/forms/form-current-address";
import { FormCurrentAddressWithEdit } from "checkout-experience/components/forms/form-current-address-with-edit";
import { FormSubTitle } from "checkout-experience/components/forms/form-sub-title";
import { FormTitle } from "checkout-experience/components/forms/form-title";
import { GooglePlacesAutocomplete } from "checkout-experience/components/google-places-autocomplete/google-places-autocomplete";
import { useCardNetworksApiLookupChex } from "checkout-experience/hooks/use-card-networks-api-lookup-chex";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import { useResetBillingOnCountryChange } from "checkout-experience/hooks/use-reset-billing-on-country-change";
import { useSupportedPaymentTypes } from "checkout-experience/hooks/use-supported-payment-types";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { requiredFieldShouldHaveAsterisk } from "checkout-experience/utils/brand-experience";
import {
  billingAddressCountrySpecificFields,
  countrySpecificFields,
  getBetterCardNetworkError,
  validateCountryCode,
} from "checkout-experience/utils/form-field-helpers";
import {
  TranslationListType,
  buildCountryList,
  buildSelectNetworkOptions,
  mapTranslationsToArrayOfValues,
} from "checkout-experience/utils/mappers/build-select-translations";
import { getIsCobrandedCardNetworksEnabled } from "checkout-experience/utils/validators/card-network";
import {
  getValidatorId,
  runValidationRulesField,
} from "checkout-experience/utils/validators/validation-rule-runner";
import { getValidatorsOrb } from "checkout-experience/utils/validators/validatorsOrb";
import { getValidatorsUpg } from "checkout-experience/utils/validators/validatorsUpg";
import {
  ChexFieldValues,
  ChexFormInputConfig,
} from "checkout-query-client/types/chex-form-fields";
import {
  FieldValues,
  InputType,
  PaymentType,
} from "checkout-query-client/types/form-types";

import { IEncryptData } from "encrypt-value/encrypt";
import { isValueEncrypted } from "encrypt-value/tools";

import { PaymentGatewayType } from "frontend-client";

import { FormInputOptions } from "jpmc-checkout-store/types";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

import { PazeButton } from "../payment-method/pay-options/paze/paze-button";
import { usePaymentOptionContext } from "../payment-method/payment-option-provider";

export const typeToInputComponentMap = {
  [InputType.text]: TextInput,
  [InputType.cardNumber]: CardNumberInput,
  // This is the mapping for the iframe field.  It is done manually below.  Eventually it may be better to swap this two and just not iframe the field when in HPP.
  // [InputType.cardNumber]: MapFieldFrame,
  [InputType.selectNetwork]: CardNetworkRadioGroup,
  [InputType.securityCode]: CVVInput,
  [InputType.checkbox]: Checkbox,
  [InputType.select]: Select,
  [InputType.dynamic]: TextInput,
  [InputType.title]: FormTitle,
  [InputType.subTitle]: FormSubTitle,
  [InputType.currentAddress]: FormCurrentAddress,
  [InputType.currentAddressWithEdit]: FormCurrentAddressWithEdit,
  [InputType.checkSampleImg]: Fragment,
  [InputType.autocomplete]: GooglePlacesAutocomplete,
  [InputType.linkWithPaze]: PazeButton,
  [InputType.consentCheckbox]: FormConsentCheckbox,
};
type InputMapType = keyof typeof typeToInputComponentMap;

const computeTranslationKey = (
  formId: string,
  fieldId: string,
  customFieldPathKeys: string,
  key: string
): string => {
  return `${formId}Form.${fieldId}${customFieldPathKeys}.${key}`;
};

export const FormField = ({
  id: fieldId,
  initialValue,
  type,
  options,
  autocomplete,
  isEmptyLabel,
  fieldIsLoading,
  fieldHasLoaded,
}: ChexFormInputConfig) => {
  const {
    t,
    i18n: { exists, language },
  } = useTranslation("combined");

  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};

  const { merchantSettings, experienceType, checkoutSession, target } =
    useChexContext();

  const isCobrandedCardsEnabled =
    getIsFeatureEnabled(target, "co-branded-cards") &&
    getIsCobrandedCardNetworksEnabled(merchantSettings);

  const supportedPaymentTypes = useSupportedPaymentTypes(
    checkoutSession?.currencyCode ?? "USD"
  );

  useResetBillingOnCountryChange();

  const formContext = useFormContext();
  const { watch, formState, control, trigger, setValue } = formContext;

  const { isFetchingTooLong } = useIsCreditCardWithSurchargeChex(formContext);

  const [showSpinner, setShowSpinner] = useAtom(showCardNumberSpinnerAtom);
  const fetchingCardType = useAtomValue(fetchingCardTypeAtom);
  const [showCheckmark, setShowCheckmark] = useState(false);
  // @ts-ignore
  // eslint-disable-next-line
  const [cardTypeApi, setCardTypeApi] = useState<
    Partial<CreditCardType> | undefined
  >();
  // @ts-ignore
  // eslint-disable-next-line
  const [cardNumberMetaDiu, setCardNumberMetaDiu] = useAtom(
    cardNumberMetaDiuAtom
  );
  // @ts-ignore - suppress unused variables error which depends on enabled
  const { isCardNetworksApiLookupTooLong, possibleCardNetworksApi } =
    isCobrandedCardsEnabled
      ? useCardNetworksApiLookupChex(formContext)
      : {
          isCardNetworksApiLookupTooLong: false,
          possibleCardNetworksApi: undefined,
        };

  const prevShowSpinner = usePrevious(showSpinner);

  const isExpiryPciEnabled = getIsFeatureEnabled(target, "q1-2025-pci-expiry");

  const featuresEnabled = { isCobrandedCardsEnabled };

  useEffect(() => {
    // Cobranded cards logic for DIU
    // useEffect to obtain cardNumberLength from field-frame
    const postMessageListener = (event: {
      data: {
        event: string;
        payload: {
          cardNumberLength: number;
          cardNumberEncryptedStrippedAndPadded: IEncryptData;
        };
      };
    }) => {
      if (event?.data?.event === "changeFieldMeta") {
        setCardNumberMetaDiu(event.data.payload);
      }
      return;
    };

    if (isCobrandedCardsEnabled && experienceType === "dropInUI") {
      window.addEventListener("message", postMessageListener);
    }
    // cleanup on unmount
    return () => {
      if (isCobrandedCardsEnabled && experienceType === "dropInUI") {
        window.removeEventListener("message", postMessageListener);
      }
    };
  }, []);

  useEffect(() => {
    if (isFetchingTooLong && experienceType === "dropInUI") {
      setShowSpinner(true);
    } else {
      setShowSpinner(false);
    }
  }, [isFetchingTooLong, experienceType]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!showSpinner && prevShowSpinner && experienceType === "dropInUI") {
      setShowCheckmark(true);
      timer = setTimeout(() => {
        setShowCheckmark(false);
      }, 5000);
    } else if (showSpinner) {
      setShowCheckmark(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSpinner, prevShowSpinner, experienceType]);

  const {
    billingAddressCountry: countryFieldValue,
    cardNumber,
    collectConsentToStoreForFuturePayments,
    expirationYear,
    expirationMonth,
    selectNetwork,
  } = (watch(`${formId}`) ?? {}) as ChexFieldValues;

  const errorMessage = (
    formState.errors[formId] as Record<string, GlobalError>
  )?.[fieldId]?.type as string;

  const cardType = useMemo(
    () => (cardNumber ? cardNumberValidator(cardNumber).cardType : undefined),
    [cardNumber]
  );

  useEffect(() => {
    // Cobranded cards logic for HPP and PBL
    if (experienceType !== "dropInUI") {
      if (isCobrandedCardsEnabled) {
        const cardTypeDetectedLib = cardNumberValidator(cardNumber).cardType;

        if (possibleCardNetworksApi && possibleCardNetworksApi.length <= 1) {
          // console.log('API has ran. 1 or less cards detected via API, use local way to display card (could be API issue, could be the only option), let the backend handle selectedPaymentType');
          setCardTypeApi(cardTypeDetectedLib);
        } else if (
          possibleCardNetworksApi &&
          possibleCardNetworksApi.length > 1 &&
          selectNetwork
        ) {
          // console.log('API has ran. More than 1 card detected via API, use API and populate cardTypeApi with full card type object')
          const cardTypeDetectedApi = creditCardType.getTypeInfo(
            niceTypesToCreditCardTypeCardBrandId[selectNetwork]
          );
          setCardTypeApi(cardTypeDetectedApi);
        } else if (!possibleCardNetworksApi && selectNetwork) {
          // console.log('User changed cardNumber to something not long enough. Use partial cardType, clear users selected network')
          setCardTypeApi({
            ...cardTypeDetectedLib,
            type: "",
            niceType: "",
          });
          setValue(`${formId}.selectNetwork`, undefined);
        } else {
          // console.log('Card number changed, no card network selected, cardNumber too short. Use partial cardType')
          setCardTypeApi({
            ...cardTypeDetectedLib,
            type: "",
            niceType: "",
          });
        }
      }
    }
  }, [
    cardNumber,
    selectNetwork,
    isCardNetworksApiLookupTooLong,
    possibleCardNetworksApi,
  ]);

  const validators = useMemo(() => {
    let validators = getValidatorsUpg(
      formId,
      supportedPaymentTypes,
      cardType,
      merchantSettings?.paymentMethods?.cardPaymentMethod?.expiryDateRequired
    );

    if (
      merchantSettings?.supportedPaymentGatewayType ===
      PaymentGatewayType.ORBITAL
    ) {
      validators = getValidatorsOrb(
        formId,
        supportedPaymentTypes,
        cardType,
        merchantSettings?.paymentMethods?.cardPaymentMethod?.expiryDateRequired
      );
    }

    if (experienceType === "dropInUI") {
      validators.cardNumber = { required: true };
      validators.accountNumber = { required: true };
      validators.confirmAccountNumber = { required: true };
      validators.bic = { required: true };
      validators.iban = { required: true };
      validators.securityCode = { required: true };
      validators.giftCardNumber = { required: true };
      validators.nameOnCard = { required: true };
      if (isExpiryPciEnabled) {
        validators.expiration = { required: true };
        validators.expirationMonth = { required: true };
        validators.expirationYear = { required: true };
      }
    }

    return validators;
  }, [supportedPaymentTypes, cardType?.lengths, formId]);

  const validatorId = useMemo(
    () => getValidatorId(fieldId, countryFieldValue),
    [fieldId, countryFieldValue, countrySpecificFields]
  );

  const optionsPath: Record<string, string> = useMemo(() => {
    const stateOptionsPathMapper: Record<string, string> = {
      US: `states_US`,
      CA: `provinces_CA`,
    };

    return countryFieldValue
      ? {
          billingAddressCountry: "countries",
          billingAddressAdministrativeArea:
            stateOptionsPathMapper[countryFieldValue],
          selectNetwork: "selectNetwork",
        }
      : ({
          billingAddressCountry: "countries",
          selectNetwork: "selectNetwork",
        } as Record<string, string>);
  }, [countryFieldValue, possibleCardNetworksApi]);

  const InputComponent = useMemo(() => {
    if (experienceType === "dropInUI") {
      if (type === InputType.cardNumber) {
        return MapFieldFrame as unknown as typeof CardNumberInput;
      } else if (
        fieldId === "iban" ||
        fieldId === "bic" ||
        fieldId === "accountNumber" ||
        fieldId === "confirmAccountNumber" ||
        fieldId === "securityCode" ||
        fieldId === "giftCardNumber" ||
        fieldId === "nameOnCard" ||
        (isExpiryPciEnabled &&
          (fieldId === "expiration" ||
            fieldId === "expirationYear" ||
            fieldId === "expirationMonth"))
      ) {
        return MapFieldFrame as unknown as typeof TextInput;
      }
    }

    // else all other experienceType's
    if (type === InputType.selectNetwork) {
      return typeToInputComponentMap[type];
    }

    return typeToInputComponentMap[
      optionsPath?.[fieldId] || options
        ? InputType.select
        : (type as InputMapType)
    ];
  }, [
    typeToInputComponentMap,
    optionsPath,
    options,
    fieldId,
    experienceType,
    type,
  ]);

  const translatedValues = {
    noCard: t("cardOptions.noCard"),
    cvv: t("cardOptions.cvv"),
    americanExpress: t("cardOptions.americanExpress"),
    discover: t("cardOptions.discover"),
    mastercard: t("cardOptions.mastercard"),
    maestro: t("cardOptions.maestro"),
    visa: t("cardOptions.visa"),
    jcb: t("cardOptions.jcb"),
    unionPay: t("cardOptions.unionPay"),
    tooltipContent:
      fieldId === "pinCode"
        ? t("giftCard.pinCodeTooltip")
        : t("cardOptions.tooltips.cvv"),
    cartesBancaires: t("cardOptions.cartesBancaires"),
    applePay: t("applePay.applePay"),
    dinersClub: t("cardOptions.dinersClub"),
    tooltipAriaLabel: t("tooltipAriaLabel"),
  };

  const isSetAsDefaultDisabled = useMemo(() => {
    return (
      fieldId === "setAsDefault" &&
      collectConsentToStoreForFuturePayments === "false"
    );
  }, [fieldId, collectConsentToStoreForFuturePayments]);

  const selectedPaymentType = useMemo(() => {
    if (isCobrandedCardsEnabled) {
      if (possibleCardNetworksApi && possibleCardNetworksApi.length <= 1) {
        return [cardType?.niceType as CreditCardNetwork];
      } else if (
        possibleCardNetworksApi &&
        possibleCardNetworksApi.length > 1 &&
        selectNetwork
      ) {
        return [selectNetwork as CreditCardNetwork];
      } else {
        return [];
      }
    } else {
      return cardType?.niceType &&
        supportedPaymentTypes?.includes(cardType.niceType as CreditCardNetwork)
        ? [cardType.niceType as CreditCardNetwork]
        : [];
    }
  }, [cardType, supportedPaymentTypes]);
  // Add additional keys to path based on requirements
  const customFieldPathKeys = useMemo(() => {
    if (!countryFieldValue) return "";

    return billingAddressCountrySpecificFields(fieldId, countryFieldValue);
  }, [fieldId, countryFieldValue]);

  const translatedTitle = useDeepCompareMemo(() => {
    let title = t(
      computeTranslationKey("checkout", fieldId, customFieldPathKeys, "title")
    );

    // title overrides go here
    if (
      fieldId === "selectNetwork" &&
      (!possibleCardNetworksApi ||
        (Array.isArray(possibleCardNetworksApi) &&
          possibleCardNetworksApi.length <= 1))
    ) {
      // hide title and when there's only one option
      title = "";
    }

    // if no label don't add star - applies to exp year
    if (title?.length > 0) {
      title += requiredFieldShouldHaveAsterisk(
        validators[validatorId],
        merchantSettings
      );
    }
    return title;
  }, [
    fieldId,
    customFieldPathKeys,
    merchantSettings,
    language,
    possibleCardNetworksApi,
  ]);

  const translatedAriaLabel =
    fieldId === "expirationMonth" || fieldId === "expirationYear"
      ? t(`checkoutForm.${fieldId}${customFieldPathKeys}.ariaLabel`)
      : null;

  const translatedPlaceholder = useMemo(
    () =>
      t(
        computeTranslationKey(
          "checkout",
          fieldId,
          customFieldPathKeys,
          "placeholder"
        )
      ),
    [fieldId, customFieldPathKeys, language, possibleCardNetworksApi]
  );

  const translatedCaption = useMemo(() => {
    const key = computeTranslationKey(
      "checkout",
      fieldId,
      customFieldPathKeys,
      "caption"
    );
    if (exists(key)) return t(key);
    return undefined;
  }, [fieldId, customFieldPathKeys, language]);

  const translatedInfo = useMemo(() => {
    const key = computeTranslationKey(
      "checkout",
      fieldId,
      customFieldPathKeys,
      "info"
    );
    if (exists(key)) return t(key);
    return undefined;
  }, [fieldId, customFieldPathKeys, language]);

  const translatedOptions = useMemo(() => {
    if (!options && optionsPath?.[fieldId]) {
      if (
        optionsPath?.[fieldId] === "countries" &&
        merchantSettings?.billingAddressCountryRestriction.length
      ) {
        const targetKeys = merchantSettings?.billingAddressCountryRestriction
          ? merchantSettings?.billingAddressCountryRestriction
          : [];

        return buildCountryList(t, targetKeys);
      }

      if (
        isCobrandedCardsEnabled &&
        optionsPath?.[fieldId] === "selectNetwork"
      ) {
        if (possibleCardNetworksApi?.length) {
          return buildSelectNetworkOptions(
            t,
            possibleCardNetworksApi,
            supportedPaymentTypes
          );
        } else {
          return;
        }
      }

      const [option, parentValue] = optionsPath[fieldId].split("_");
      const translations = t(
        parentValue
          ? `${parentValue.toLowerCase()}-${option}.${option}`
          : `${option}.${option}`,
        {
          returnObjects: true,
        }
      ) as TranslationListType;

      return mapTranslationsToArrayOfValues(translations);
    }

    return options?.map((option: FormInputOptions) => ({
      ...option,
      title: t(option.title),
    }));
  }, [
    options,
    optionsPath,
    merchantSettings?.billingAddressCountryRestriction,
    language,
    possibleCardNetworksApi,
  ]);

  const content = useMemo(
    () => (type === InputType.checkbox ? translatedTitle : ""),
    [type, translatedTitle]
  );

  let translatedErrorMessage =
    errorMessage &&
    t(`checkoutForm.${fieldId}${customFieldPathKeys}.errors.${errorMessage}`);

  if (errorMessage && errorMessage?.indexOf("cardNetwork") >= 0) {
    let calcCardType = cardType?.niceType;
    let calcErrorMessage = "cardNetwork";
    if (errorMessage.indexOf("-") > 0) {
      calcCardType = errorMessage.split("-")[1];
      calcErrorMessage = errorMessage.split("-")[0];
    }
    translatedErrorMessage = getBetterCardNetworkError(
      t,
      exists,
      `${fieldId}${customFieldPathKeys}`,
      calcErrorMessage || "",
      calcCardType || "",
      supportedPaymentTypes ?? []
    );
  }

  const hasCurrentShippingAddress = useMemo(() => {
    const billingAddressCollection = {
      [PaymentType.creditCard]:
        merchantSettings?.paymentMethods?.cardPaymentMethod
          ?.billingAddressCollection,
      [PaymentType.eCheck]:
        merchantSettings?.paymentMethods?.ecpPaymentMethod
          ?.billingAddressCollection,
      [PaymentType.sepa]:
        merchantSettings?.paymentMethods?.sepaPaymentMethod
          ?.billingAddressCollection,
      [PaymentType.applePay]: undefined,
      [PaymentType.googlePay]: undefined,
      [PaymentType.paze]: undefined,
      [PaymentType.payByBank]: undefined,
      [PaymentType.ideal]: undefined,
      [PaymentType.alipay]: undefined,
      [PaymentType.paypal]: undefined,
      [PaymentType.wechatPay]: undefined,
      [PaymentType.trustly]: undefined,
      [PaymentType.giftCard]: undefined,
    };

    return paymentType && type?.startsWith("currentAddress")
      ? {
          currentShippingAddress: {
            paymentType,
            shippingAddress: checkoutSession?.shippingAddress,
            billingAddress: checkoutSession?.paymentMethods?.find(
              (p) => p.paymentMethodId === formId
            )?.billingAddress,
            billingAddressCollection: billingAddressCollection[paymentType],
          },
        }
      : {};
  }, [paymentType, type, checkoutSession, merchantSettings]);

  const modifyValue = (value: string | undefined) => {
    if (value) {
      if (fieldId === "cardNumber") {
        return prettifyUICardNumber(`${value}`, cardType?.gaps ?? []);
      } else if (fieldId === "expiration") {
        return prettifyExpirationValue(`${value}`);
      } else if (fieldId === "expirationMonth") {
        return padExpirationMonthValue(`${value}`);
      } else if (fieldId === "iban") {
        return friendlyFormatIBAN(value) ?? undefined;
      } else if (fieldId === "billingAddressCountry") {
        return validateCountryCode(value) ?? undefined;
      }
    }
    return value;
  };
  const handleOnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: ControllerRenderProps<FieldValues<string>, string>
  ) => {
    let newValue: string | undefined = undefined;
    if (type === InputType.checkbox || type === InputType.consentCheckbox) {
      if ((e.target as HTMLInputElement).checked) {
        newValue = "true";
      } else {
        newValue = "false";
      }
    } else if (type === InputType.selectNetwork) {
      newValue = e.target.value;
      setValue(`${formId}.selectNetwork`, newValue as CreditCardNetwork);
    } else {
      newValue = runValidationRulesField({
        validators,
        validatorId,
        // Run validations on the formatted/modified expiry value but not if encrypted
        newValue:
          fieldId === "expiration" && !isValueEncrypted(e.target.value)
            ? (modifyValue(e.target.value) ?? "")
            : e.target.value,
        field: field,
      });
    }
    field.onChange(newValue);
  };

  useEffect(() => {
    if (expirationMonth) {
      trigger(`${formId}.expirationMonth`).catch((err) => logger.error(err));
    }

    if (expirationYear) {
      trigger(`${formId}.expirationYear`).catch((err) => logger.error(err));
    }
  }, [expirationYear, expirationMonth]);

  // This is for Field Frame max length property
  const maxLengthForField = useMemo((): number | undefined => {
    // Only do name on card
    if (fieldId !== "nameOnCard") {
      return undefined;
    }
    if (
      merchantSettings?.supportedPaymentGatewayType ===
      PaymentGatewayType.ORBITAL
    ) {
      return getValidatorsOrb()[fieldId]?.maxLength as number;
    }
    if (
      merchantSettings?.supportedPaymentGatewayType === PaymentGatewayType.UPG
    ) {
      return getValidatorsUpg()[fieldId]?.maxLength as number;
    }
    return undefined;
  }, [merchantSettings]);

  ////////////////////////
  // START TEMP DEBUG INFO
  // useEffect(() => {
  //   console.log("cardTypeApi changed", cardTypeApi);
  // }, [cardTypeApi]);

  // useEffect(() => {
  //   console.log("selectNetwork changed", selectNetwork);
  // }, [selectNetwork]);

  // useEffect(() => {
  //   console.log("translatedOptions changed", translatedOptions);
  // }, [translatedOptions]);
  // console.log(
  //   "-----> feature flags",
  //   isCobrandedCardsEnabled,
  //   "[",
  //   getIsFeatureEnabled(target, "co-branded-cards"),
  //   getIsCobrandedCardNetworksEnabled(merchantSettings),
  //   "]"
  // );
  // END TEMP DEBUG INFO
  //////////////////////

  if (!isCobrandedCardsEnabled && type === InputType.selectNetwork) return;

  return (
    <>
      {type === InputType.checkSampleImg ? (
        language === "es-US" ? (
          <CheckExampleEsUS />
        ) : (
          <CheckExampleEn role="presentation" />
        )
      ) : (
        <Controller
          name={`${formId}.${fieldId}`}
          control={control}
          rules={validators[validatorId]}
          defaultValue={initialValue}
          render={({ field }) => {
            const isChecked =
              type === InputType.checkbox
                ? { isChecked: field.value === "true" }
                : {};
            return (
              <InputComponent
                id={fieldId}
                title={translatedTitle}
                ariaLabel={translatedAriaLabel ?? translatedTitle}
                placeholder={translatedPlaceholder}
                captionMessage={translatedCaption}
                info={translatedInfo}
                options={translatedOptions || []}
                errorMessageKey={errorMessage}
                errorMessage={translatedErrorMessage}
                autocomplete={autocomplete}
                isDisabled={isSetAsDefaultDisabled}
                content={content}
                {...hasCurrentShippingAddress}
                {...isChecked}
                selectedPaymentType={selectedPaymentType}
                supportedPaymentTypes={supportedPaymentTypes || []}
                isEmptyLabel={isEmptyLabel}
                {...field}
                value={modifyValue(field.value as string)}
                onChange={(e) => handleOnChange(e, field)}
                {...{ isReady: fieldHasLoaded }}
                {...{ isLoading: fieldIsLoading }}
                translatedValues={translatedValues}
                showSpinner={showSpinner && experienceType === "dropInUI"}
                showCheckmark={showCheckmark}
                fetchingCardType={fetchingCardType}
                {...(maxLengthForField ? { maxLength: maxLengthForField } : {})} // only append if it has a value
                featuresEnabled={featuresEnabled}
              />
            );
          }}
        />
      )}
    </>
  );
};
